import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, RotateCcw } from "lucide-react";
import Reveal, { EASE } from "../components/Reveal";

const QUESTIONS = [
  "Can a visitor immediately explain what you do?",
  "Can they tell who your service is for?",
  "Can they understand why you're different?",
  "Can they see evidence that they should trust you?",
  "Do they know exactly what to do next?",
];

const ANSWERS = ["Yes", "Not Really", "No"];

const RESULTS = [
  {
    min: 4,
    headline: "Your story is coming through.",
    copy: "Your digital presence appears to communicate the essentials. The question is whether it's telling that story as well as your business deserves.",
  },
  {
    min: 2,
    headline: "Your story may need clarity.",
    copy: "Your business may be stronger than the way it's currently being communicated.",
  },
  {
    min: 0,
    headline: "Your story isn't being heard.",
    copy: "Right now, customers likely have to work harder than they should to understand your value.",
  },
];

export default function StoryDiagnostic() {
  const [answers, setAnswers] = useState([]);
  const done = answers.length === QUESTIONS.length;
  const yesCount = answers.filter((a) => a === "Yes").length;
  const result = RESULTS.find((r) => yesCount >= r.min);

  const answer = (value) => {
    if (done) return;
    setAnswers((prev) => [...prev, value]);
  };

  return (
    <section data-testid="story-diagnostic-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="font-mono text-xs font-semibold tracking-[0.3em] text-gold">A QUICK DIAGNOSTIC</p>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Can a customer understand your business in 10 seconds?
          </h2>
          <p className="mt-4 text-base text-slate-400">Five honest questions. Answer for your current website.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 rounded-3xl border border-navyline bg-navycard p-7 sm:p-10">
            <div className="mb-8 flex justify-center gap-2">
              {QUESTIONS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i < answers.length ? "w-8 bg-gold" : i === answers.length ? "w-8 bg-gold/40" : "w-8 bg-navyline"
                  }`}
                  aria-hidden="true"
                />
              ))}
            </div>
            <div className="min-h-[190px]">
              <AnimatePresence mode="wait">
                {!done ? (
                  <motion.div
                    key={answers.length}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.25, ease: EASE }}
                  >
                    <p className="text-center font-mono text-[10px] tracking-[0.24em] text-slate-500">
                      QUESTION {answers.length + 1} OF {QUESTIONS.length}
                    </p>
                    <p
                      data-testid="diagnostic-question"
                      className="mt-4 text-center font-display text-xl font-semibold leading-snug text-white sm:text-2xl"
                    >
                      {QUESTIONS[answers.length]}
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                      {ANSWERS.map((a) => (
                        <button
                          key={a}
                          data-testid={`diagnostic-answer-${a.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => answer(a)}
                          className={`rounded-full border px-7 py-3 text-sm font-semibold transition-colors duration-200 ${
                            a === "Yes"
                              ? "border-gold/60 text-gold hover:bg-gold hover:text-ink"
                              : "border-navyline text-slate-300 hover:border-gold/50 hover:text-gold"
                          }`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="result"
                    data-testid="diagnostic-result"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className="text-center"
                  >
                    <p className="font-mono text-[10px] tracking-[0.24em] text-slate-500">
                      YOU ANSWERED YES TO {yesCount} OF {QUESTIONS.length}
                    </p>
                    <h3 className="text-gold-gradient mt-4 font-display text-2xl font-extrabold sm:text-3xl">
                      {result.headline}
                    </h3>
                    <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-400">{result.copy}</p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                      <Link
                        to="/start"
                        data-testid="diagnostic-start-cta"
                        className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-ink transition-colors duration-200 hover:bg-goldlight"
                      >
                        Tell Us Your Story
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                      <button
                        data-testid="diagnostic-retake-btn"
                        onClick={() => setAnswers([])}
                        className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors duration-200 hover:text-gold"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Retake
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
